import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Badge, RegionState, SkeletonLoader } from '@freelancelabs/components';

import { ListSkeleton } from '../../components/Skeletons';
import { OrganizationKind } from '../../graphql/__generated__/schema';
import { AccessibleAccountsDocument } from '../../graphql/__generated__/types';
import DashboardCataloguePanel from '../catalogues/DashboardCataloguePanel';
import AccountModulesPanel from './AccountModulesPanel';
import MfaRequirementPanel from './MfaRequirementPanel';
import PortalAccessPanel from './PortalAccessPanel';
import { accountLookupRefusalOf } from './accountsRefusal';

/**
 * The sheet of one account, home to the configuration panels.
 *
 * The account is named through `accessibleAccounts`, not through the list this sheet is
 * opened from: `client-hub-api` has no single-account read, and its `Account` carries no
 * `kind`, which the panels need in order to drop out on Freelance.com. `PortalAccessDialog`
 * refetches that document, so opening or closing the account updates this header on its own.
 */
const AccountDetailPage = () => {
    const { accountId } = useParams<{ accountId: string }>();

    const { data, loading, error } = useQuery(AccessibleAccountsDocument);

    const account = (data?.accessibleAccounts ?? []).find((candidate) => candidate?.id === accountId);

    const backToList = (
        <Link to="/comptes" className="link text-sm">
            {c('Accounts').t`Back to the accounts`}
        </Link>
    );

    if (loading && !data) {
        return (
            <section className="flex flex-col gap-5">
                {backToList}
                {/* Four skeleton panels because the sheet carries four, so the page does
                    not jump when the response arrives. */}
                <div className="flex flex-col gap-5" aria-busy="true">
                    <SkeletonLoader className="w-1/3" height={26} />
                    <SkeletonLoader className="w-1/4" height={14} />
                    {[0, 1, 2, 3].map((rang) => (
                        <div className="rounded-xl border border-border-norm p-5" key={rang}>
                            <SkeletonLoader className="mb-4 w-1/4" height={18} />
                            <ListSkeleton rows={3} />
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    if (error) {
        const refusal = accountLookupRefusalOf(error);
        return (
            <section className="flex flex-col gap-5">
                {backToList}
                <RegionState
                    tone="error"
                    title={refusal.title}
                    description={
                        <>
                            {refusal.guidance}
                            {refusal.detail && (
                                <span className="mt-2 block break-words rounded-md border border-border-norm bg-background-weak p-2 font-mono text-xs">
                                    {refusal.detail}
                                </span>
                            )}
                        </>
                    }
                />
            </section>
        );
    }

    // Not found: the account may not exist or sit outside the portfolio, and the screen
    // cannot know which (the perimeter is what it looks the account up in), so the
    // sentence says exactly that rather than guessing.
    if (!account?.id) {
        return (
            <section className="flex flex-col gap-5">
                {backToList}
                <RegionState
                    title={c('Accounts').t`This account is not one you can act on.`}
                    description={c('Accounts')
                        .t`Either it does not exist, or it is outside your portfolio, and the address alone cannot tell which, since accounts are looked up among the ones you may act on. If you reached this page from a link somebody sent you, ask them which account it was.`}
                />
            </section>
        );
    }

    const name = account.name ?? account.id;
    // ttag refuses an expression inside a localized template, so the name is bound to a
    // plain identifier before the string that holds it.
    const companyName = account.rootOrganizationName;

    // Freelance.com is not a client account: every panel below would refuse it, so the
    // sheet says so rather than rendering a heading over nothing.
    if (account.kind === OrganizationKind.Internal) {
        return (
            <section className="flex flex-col gap-5">
                {backToList}
                <h1 className="text-xl font-semibold text-text-900">{name}</h1>
                <RegionState
                    title={c('Accounts').t`Freelance.com is not a client account.`}
                    description={c('Accounts')
                        .t`It carries no portal access, no portal module and no dashboard catalogue: the back-office is a separate application and none of them gates it. That is why it is absent from the accounts list, and it stays in your perimeter so that you can administer internal colleagues from the users screen.`}
                />
            </section>
        );
    }

    return (
        <section className="flex flex-col gap-5">
            {backToList}

            <header className="flex flex-wrap items-start gap-4">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl font-semibold text-text-900">{name}</h1>
                        {/* Null is not « fermé »: a missing field would announce a state
                            nobody set, on the very fact the first panel exists to change. */}
                        {account.portalEnabled !== null && account.portalEnabled !== undefined && (
                            <Badge category={account.portalEnabled ? 'success' : 'default'} size="small">
                                {account.portalEnabled
                                    ? c('Portal access').t`Open to the portal`
                                    : c('Portal access').t`Closed to the portal`}
                            </Badge>
                        )}
                    </div>
                    {/* The company, read and never linked (its sheet is not always
                        reachable). Shown only when the account is not its own root. */}
                    {account.rootOrganizationId && account.rootOrganizationId !== account.id && (
                        <p className="mt-1 text-xs text-text-600">
                            {companyName
                                ? c('Accounts').t`Head office: ${companyName}`
                                : c('Accounts').t`Belongs to a company this list does not name.`}
                        </p>
                    )}
                </div>
            </header>

            <PortalAccessPanel
                accountId={account.id}
                accountName={name}
                portalEnabled={account.portalEnabled}
                kind={account.kind}
            />
            {/* After access, before modules: the panels read as a sequence of decisions on
                a client, from the most general to the most precise. */}
            <MfaRequirementPanel
                accountId={account.id}
                accountName={name}
                mfaRequired={account.mfaRequired}
                kind={account.kind}
            />
            <AccountModulesPanel accountId={account.id} accountName={name} kind={account.kind} />
            <DashboardCataloguePanel accountId={account.id} accountName={name} kind={account.kind} />
        </section>
    );
};

export default AccountDetailPage;
